import React, { useState, useEffect } from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import ActionButton from './ui/action-button';
import { Upload, Eye, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import DashboardPageLayout from './DashboardPageLayout';
import FormSection from './FormSection';

const BrandingTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  const [logoUrl, setLogoUrl] = useState('');
  const [logoPreview, setLogoPreview] = useState('');
  const [primaryColor, setPrimaryColor] = useState('#007AFF');
  const [orgData, setOrgData] = useState(null);

  useEffect(() => {
    fetchOrgData();
  }, []);

  const fetchOrgData = async () => {
    try {
      setLoading(true);
      const orgId = localStorage.getItem('organizationId');
      const response = await api.get(`/organizations/${orgId}`);
      const data = response.data;
      
      setOrgData(data);
      setLogoUrl(data.logoUrl || '');
      setLogoPreview(data.logoUrl || '');
      setPrimaryColor(data.primaryColor || '#007AFF');
    } catch (error) {
      console.error('Error fetching organization:', error);
      toast.error('Failed to load branding settings');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Logo must be less than 5MB');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const orgId = localStorage.getItem('organizationId');
      const response = await api.post(`/organizations/${orgId}/upload-logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const uploadedLogoUrl = response.data.logoUrl;
      setLogoUrl(uploadedLogoUrl);
      setLogoPreview(uploadedLogoUrl);
      toast.success('Logo uploaded! Click "Save Changes" to apply.');
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error('Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const orgId = localStorage.getItem('organizationId');
      await api.put(`/organizations/${orgId}`, {
        logoUrl: logoUrl,
        primaryColor: primaryColor
      });

      toast.success('Branding saved successfully!');
    } catch (error) {
      console.error('Error saving branding:', error);
      toast.error('Failed to save branding');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <DashboardPageLayout
      title="Branding"
      subtitle="Customize how your booking widget looks to customers"
      actions={
        <ActionButton
          onClick={handleSave}
          loading={saving}
          icon={CheckCircle}
        >
          Save Changes
        </ActionButton>
      }
    >
      <div className="space-y-6">
        {/* STEP 1: Logo Upload */}
        <FormSection
          stepNumber={1}
          title="Upload Your Logo"
          description="Your logo appears on the booking widget and customer tickets"
          color="#3B82F6"
        >
          <div className="space-y-4">
            {logoPreview && (
              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-md border border-gray-200">
                <img 
                  src={logoPreview} 
                  alt="Logo" 
                  className="h-12 w-auto object-contain"
                />
              </div>
            )}
            
            <div className="flex items-center gap-3">
              <input
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                className="hidden"
                id="logo-upload"
              />
              <label 
                htmlFor="logo-upload"
                className="text-gray-700 opacity-80 hover:opacity-100 cursor-pointer font-medium text-lg px-6 py-3 border border-gray-300 rounded-md hover:bg-gray-50 transition-all"
              >
                {uploading ? 'Uploading...' : 'Choose Image'}
              </label>
              <span className="text-base text-gray-500">PNG, JPG up to 5MB</span>
            </div>
          </div>
        </FormSection>

        {/* STEP 2: Brand Color */}
        <FormSection
          stepNumber={2}
          title="Choose Your Brand Color"
          description="Primary color for buttons and accents on your booking widget"
          color="#9333EA"
        >
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <Input
                type="color"
                value={primaryColor}
                onChange={(e) => setPrimaryColor(e.target.value)}
                className="h-12 w-20 cursor-pointer p-1"
              />
              <Input
                type="text"
                value={primaryColor}
                onChange={(e) => setPrimaryColor(e.target.value)}
                placeholder="#004cff"
                className="font-mono flex-1 text-lg"
              />
            </div>
            
            <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-md border border-gray-200">
              <span className="text-lg text-gray-600">Preview</span>
              <div 
                className="w-7 h-7 rounded-full flex-shrink-0" 
                style={{ background: primaryColor }}
              />
              <span className="text-lg text-gray-600">Primary accent color</span>
            </div>
          </div>
        </FormSection>

        {/* STEP 3: Preview */}
        <FormSection
          stepNumber={3}
          title="Preview Your Branding"
          description="See how customers will see your booking widget"
          color="#10B981"
        >
          {orgData?.logoUrl ? (
            <button
              onClick={() => window.open(`/widget-v3.html?org=${orgData.id}`, '_blank')}
              className="flex items-center gap-2 text-gray-700 opacity-80 hover:opacity-100 font-medium text-lg px-6 py-3 border border-gray-300 rounded-md hover:bg-gray-50 transition-all"
            >
              <Eye className="w-5 h-5" />
              Preview Booking Widget
            </button>
          ) : (
            <p className="text-lg text-gray-500">Upload a logo to preview your booking widget</p>
          )}
        </FormSection>
      </div>
    </DashboardPageLayout>
  );
};

export default BrandingTab;
